import type { Kind } from '~/components/upload2';
import type { UploadResult } from '~/components/upload2/Upload';

/**
 * Image Conversion Utilities
 *
 * These functions handle the conversion between legacy image storage formats and the modern
 * UploadResult format used by forms.
 *
 * Background: The app has evolved from storing simple image ID strings to a more complex
 * UploadResult structure that includes preview URLs, names, and metadata. These utilities
 * bridge that gap by converting legacy data to the expected modern format.
 */

/**
 * Converts a legacy image ID/path to an UploadResult object
 *
 * This handles the basic conversion of a single image identifier (which could be a simple ID
 * or a full path) into the UploadResult format expected by upload components.
 *
 * DEFENSIVE: Validates input and uses fallbacks to prevent crashes
 *
 * @param imgId - The image ID or path from legacy storage
 * @param fallbackName - Optional fallback name if extraction fails
 * @returns UploadResult object ready for form use
 */
export const imageStringToUploadResult = (
  imgId: string,
  fallbackName = 'image.jpg'
): UploadResult => {
  // Defensive: Ensure imgId is a valid string
  if (!imgId || typeof imgId !== 'string' || imgId.trim() === '') {
    console.warn('Invalid image ID provided, using fallback');
    return {
      id: fallbackName,
      preview: '',
      name: fallbackName,
      kind: 'image' as Kind,
    };
  }

  try {
    const previewUrl =
      imgId.startsWith('http') || imgId.startsWith('blob:') ? imgId : imgId;
    const name = imgId.substring(imgId.lastIndexOf('/') + 1) || fallbackName;

    return {
      id: imgId,
      preview: previewUrl,
      name: name,
      kind: 'image' as Kind,
    };
  } catch (error) {
    console.error('Error converting image string to upload result:', error);
    return {
      id: fallbackName,
      preview: '',
      name: fallbackName,
      kind: 'image' as Kind,
    };
  }
};

/**
 * Converts legacy image data structure to UploadResult array for form initialization
 *
 * This is the main workhorse function that was duplicated across ItemForm and similar components.
 * It handles the complex logic of matching primary image URLs with their corresponding IDs,
 * processing multiple image arrays, and building the complete UploadResult structure.
 *
 * The legacy system often stores:
 * - A primary image as a full URL (for display)
 * - An array of image IDs (for storage/reference)
 * - These two pieces of data need to be correlated and converted to UploadResult format
 *
 * @param options - Configuration object with image data from legacy storage
 * @returns Array of UploadResult objects ready for form initialization
 */
export const convertLegacyImagesToUploadResults = ({
  primaryImageUrl,
  imageIds = [],
  fallbackName = 'image.jpg',
  maxResults,
}: {
  primaryImageUrl?: string | null;
  imageIds?: string[];
  fallbackName?: string;
  maxResults?: number;
}): UploadResult[] => {
  const result: UploadResult[] = [];

  if (primaryImageUrl) {
    let idForPrimary = primaryImageUrl.substring(
      primaryImageUrl.lastIndexOf('/') + 1
    );
    let nameForPrimary = idForPrimary || fallbackName;

    if (imageIds.length > 0) {
      idForPrimary = imageIds[0]; // Assume first ID corresponds to primaryImageUrl
      // Extract filename if idForPrimary is a path
      nameForPrimary = idForPrimary.includes('/')
        ? idForPrimary.substring(idForPrimary.lastIndexOf('/') + 1)
        : idForPrimary;
    }

    result.push({
      id: idForPrimary,
      preview: primaryImageUrl, // Use direct URL for preview
      name: nameForPrimary || fallbackName,
      kind: 'image' as Kind,
    });

    // Process remaining image IDs (if any)
    const remainingIds = imageIds.slice(1);
    remainingIds.forEach((imgId) => {
      result.push(imageStringToUploadResult(imgId, fallbackName));
    });
  } else {
    // No primaryImageUrl, so process all IDs from imageIds
    imageIds.forEach((imgId) => {
      result.push(imageStringToUploadResult(imgId, fallbackName));
    });
  }

  // Apply maxResults limit if specified
  return maxResults ? result.slice(0, maxResults) : result;
};

/**
 * Simple conversion for asset images (single image case)
 *
 * Assets typically have a simpler image structure compared to items - usually just one image.
 * This function was extracted from AssetForm.tsx where it was implemented inline.
 * It handles the common pattern of assets having both an image URL and an images array.
 *
 * DEFENSIVE: Returns empty array if image data is malformed or broken to prevent crashes
 *
 * @param asset - Asset object with image data from GraphQL
 * @returns Array of UploadResult objects (0 or 1 item)
 */
export const convertAssetImagesToUploadResults = (asset?: {
  images?: string[];
  image?: string | null;
}): UploadResult[] => {
  // Defensive checks to prevent crashes with broken/malformed images
  if (!asset?.images?.length || !asset.images[0] || !asset.image) {
    return [];
  }

  // Additional validation: ensure image ID and URL are valid strings
  const imageId = asset.images[0];
  const imageUrl = asset.image;

  if (
    typeof imageId !== 'string' ||
    typeof imageUrl !== 'string' ||
    imageId.trim() === '' ||
    imageUrl.trim() === ''
  ) {
    console.warn('Invalid image data detected, skipping conversion:', {
      imageId,
      imageUrl,
    });
    return [];
  }

  try {
    return [
      {
        id: imageId,
        preview: imageUrl,
        name: 'Current Image',
        kind: 'image' as Kind,
      },
    ];
  } catch (error) {
    console.error('Error converting asset images to upload results:', error);
    return [];
  }
};

/**
 * Converts variant image data to UploadResult array
 *
 * Product variants (SKUs) have their own image handling logic that was duplicated in ItemForm.
 * This function encapsulates the complex fallback logic where variants can have:
 * 1. Both a primary image URL and an images array (preferred)
 * 2. Only a primary image URL (fallback case)
 * 3. No images at all (empty result)
 *
 * @param variant - Variant/SKU object with image data from GraphQL
 * @returns Array of UploadResult objects (0 or 1 item)
 */
export const convertVariantImagesToUploadResults = (variant?: {
  image?: string | null;
  images?: string[];
}): UploadResult[] => {
  if (!variant) return [];

  const processedVariantImages: UploadResult[] = [];
  const primaryVariantDirectUrl = variant.image;
  const allVariantImageIds = variant.images ?? [];

  if (primaryVariantDirectUrl && allVariantImageIds.length > 0) {
    const imageId = allVariantImageIds[0];
    const imageName =
      imageId.substring(imageId.lastIndexOf('/') + 1) || 'variant_image.jpg';

    processedVariantImages.push({
      id: imageId,
      preview: primaryVariantDirectUrl,
      name: imageName,
      kind: 'image' as Kind,
    });
  } else if (primaryVariantDirectUrl) {
    // Fallback: variant.image (full URL) exists, but variant.images array is empty
    const derivedNameAndId =
      primaryVariantDirectUrl.substring(
        primaryVariantDirectUrl.lastIndexOf('/') + 1
      ) || 'variant_image.jpg';

    processedVariantImages.push({
      id: derivedNameAndId,
      preview: primaryVariantDirectUrl,
      name: derivedNameAndId,
      kind: 'image' as Kind,
    });
  }

  return processedVariantImages;
};

/**
 * Converts UploadResult back to image string (for form submission)
 *
 * This is the reverse operation - when forms are submitted, they need to convert
 * the UploadResult objects back to simple ID strings for GraphQL mutations.
 * This was also duplicated across multiple forms as a simple inline function.
 *
 * @param uploadResult - UploadResult object from form state
 * @returns Image ID string for GraphQL mutation
 */
export const uploadResultToImageString = (
  uploadResult: UploadResult
): string => {
  return uploadResult.id;
};
